import React, { useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

const isExcelFile = file => /\.(xlsx|xls)$/i.test(file.name);

const FileDrop = ({ file, onFileChange, home = false, disabled = false, message = '' }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState('');

  const chooseFile = selectedFile => {
    if (!selectedFile) {
      return;
    }
    if (!isExcelFile(selectedFile)) {
      setFileError('Choose an .xlsx or .xls file.');
      return;
    }
    setFileError('');
    onFileChange(selectedFile);
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleAreaClick = event => {
    if (!event.target.closest?.('button')) openPicker();
  };

  const handleAreaKeyDown = event => {
    if (event.target.closest?.('button')) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const handleDrop = event => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) {
      chooseFile(event.dataTransfer.files[0]);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleAreaClick}
      onKeyDown={handleAreaKeyDown}
      onDragOver={event => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: home ? 1.75 : 1.5,
        minHeight: home ? 76 : 70,
        border: '1px dashed',
        borderColor: dragging ? 'primary.main' : '#83d0c8',
        borderRadius: home ? '14px' : '12px',
        bgcolor: dragging ? 'primary.light' : '#f6fbfa',
        transition: 'background-color .15s ease, border-color .15s ease'
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          flex: '0 0 auto',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '10px',
          bgcolor: 'primary.light',
          color: 'primary.main'
        }}
      >
        {file ? <InsertDriveFileOutlinedIcon /> : <CloudUploadOutlinedIcon />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, overflowWrap: 'anywhere' }}>
          {file ? file.name : home ? 'Drop an Excel file here' : 'Choose an Excel file'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {file ? 'Ready to check · ' : home ? 'Or pick a type below. ' : ''}
          {!file && (home ? '.xlsx or .xls, up to 10 MB.' : '.xlsx or .xls, up to 10 MB.')}
          {file && (
            <Button
              size="small"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              sx={{ p: 0, minWidth: 0, ml: 0.5, fontSize: 'inherit', verticalAlign: 'baseline' }}
            >
              Replace file
            </Button>
          )}
        </Typography>
        {(message || fileError) && (
          <Typography variant="caption" color={fileError ? 'error.main' : 'text.secondary'} display="block">
            {fileError || message}
          </Typography>
        )}
      </Box>
      {!file && (
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={openPicker}
          sx={{ flex: '0 0 auto' }}
        >
          Browse
        </Button>
      )}
      <input
        ref={inputRef}
        hidden
        type="file"
        accept=".xlsx,.xls"
        disabled={disabled}
        onChange={event => {
          chooseFile(event.target.files[0]);
          event.target.value = '';
        }}
      />
    </Box>
  );
};

export default FileDrop;
